<template>
  <div>
    <SoPageHead :title="$t('securone.nav.events')" :subtitle="$t('securone.events.subtitle')">
      <select v-if="showDepartmentFilter" v-model="departmentId" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.department.filter_all") }}</option>
        <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
      </select>
      <div class="sd-tabs inline-flex">
        <button class="sd-tabs__trigger" :class="{ 'sd-tabs__trigger--active': filter === 'active' }" @click="filter = 'active'">{{ $t("securone.events.active") }}</button>
        <button class="sd-tabs__trigger" :class="{ 'sd-tabs__trigger--active': filter === 'resolved' }" @click="filter = 'resolved'">{{ $t("securone.events.resolved") }}</button>
      </div>
    </SoPageHead>

    <SoNote tone="muted" class="mb-4">{{ $t("securone.events.read_only_note") }}</SoNote>

    <div class="sd-card overflow-hidden mb-4">
      <div v-if="loading" class="px-4 py-8 text-center text-gray-400 text-sm">{{ $t("securone.common.loading") }}</div>
      <template v-else-if="events.length">
        <router-link v-for="ev in events" :key="ev._id" :to="{ name: 'securone_event', params: { id: ev._id } }" class="flex items-center gap-4 px-4 py-3 border-b border-secondary-100 last:border-0 hover:bg-gray-50 cursor-pointer">
          <span class="w-2 h-2 rounded-full flex-shrink-0" :class="ev.status === 'active' ? 'bg-red-500' : 'bg-gray-300'"></span>
          <div class="flex-1 min-w-0">
            <div class="font-semibold text-gray-800 truncate">{{ ev.category.name }}</div>
            <div class="text-xs text-gray-500 truncate mt-0.5">{{ $t("securone.events.sent_by", { name: ev.raisedByName }) }}<template v-if="ev.departmentName"> · {{ ev.departmentName }}</template><template v-if="ev.lastMessagePreview"> · {{ ev.lastMessagePreview }}</template></div>
          </div>
          <div class="w-40 text-xs text-gray-600 hidden md:block">
            <template v-if="ev.status === 'active'">{{ $t("securone.events.acknowledged_of", { n: ev.acknowledgedCount, total: ev.recipientCount }) }}</template>
            <template v-else>{{ $t("securone.events.resolved_by", { name: ev.resolvedByName }) }}</template>
          </div>
          <div class="w-24 text-right font-mono text-sm" :class="ev.status === 'active' ? 'text-red-700' : 'text-gray-400'">
            <template v-if="ev.status === 'active'">{{ elapsedSince(ev.raisedAt || ev.createdAt) }}</template>
            <template v-else>{{ dateStamp(ev.resolvedAt) }}</template>
          </div>
        </router-link>
      </template>
      <div v-else class="px-4 py-8 text-center text-gray-400 text-sm">
        {{ filter === "active" ? $t("securone.events.no_active") : $t("securone.events.no_resolved") }}
      </div>
    </div>
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoNote from "./components/SoNote.vue";
import api from "./securone.api";
import * as socket from "./securone.socket";
import { tickerMixin } from "./securone.helpers";
import { POLL } from "./securone.constants";

// Read-only event history (plan §11.3, WP9). Active / Resolved filter, one
// container, hairline rows. Nothing is sent, acknowledged or resolved here.
// Live: the socket pings when an event is raised or changes and the list
// refetches; the active list also polls, at the rate securone.constants picks
// from whether the socket is up.
export default {
  name: "SecurOneEvents",
  components: { SoPageHead, SoNote },
  mixins: [tickerMixin],
  data() {
    return { filter: "active", events: [], loading: false, departmentId: this.$store.getters["securone/departmentFilter"] || "", unsubscribe: [], poll: null, refreshTimer: null };
  },
  computed: {
    departments() {
      return this.$store.getters["securone/departments"];
    },
    showDepartmentFilter() {
      return this.$store.getters["securone/showDepartmentFilter"];
    },
  },
  methods: {
    async load(quiet = false) {
      if (!quiet) this.loading = true;
      try {
        this.events = await api.events(this.filter, { departmentId: this.departmentId || undefined });
      } catch (error) {
        if (!quiet) this.handleError(error);
      } finally {
        this.loading = false;
        this.schedulePoll();
      }
    },
    refresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.load(true), 150);
    },
    schedulePoll() {
      clearTimeout(this.poll);
      this.poll = null;
      if (this.filter !== "active") return;
      this.poll = setTimeout(() => this.load(true), socket.isConnected() ? POLL.eventsSocket : POLL.eventsNoSocket);
    },
  },
  watch: {
    filter() {
      this.load();
    },
    departmentId(value) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", value || null);
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.events"), "securone_events");
    this.load();
    this.unsubscribe.push(socket.on("securone.events.changed", () => this.refresh()));
  },
  beforeDestroy() {
    this.unsubscribe.forEach((fn) => fn());
    clearTimeout(this.poll);
    clearTimeout(this.refreshTimer);
  },
};
</script>
